import React, { useState, useEffect, useCallback } from 'react';
import WorkstreamForm from './WorkstreamForm';
import CommentsPanel from './CommentsPanel';
import ActivityTimeline from './ActivityTimeline';
import { DocumentSection } from './DocumentSection';
import { useProjects } from '../contexts/ProjectsContext';
import '../styles/Projects.css';

const MAPBOX_TOKEN = import.meta.env?.VITE_MAPBOX_TOKEN || '';
const mapUrl = (lat, lon) =>
  `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/pin-s+ef4444(${lon},${lat})/${lon},${lat},14,0/400x200@2x?access_token=${MAPBOX_TOKEN}`;

const STAGES = [
  { key: 'candidate', label: 'Candidate' },
  { key: 'screening', label: 'Screening' },
  { key: 'land_contact', label: 'Land Contact' },
  { key: 'option_signed', label: 'Option Signed' },
  { key: 'grid_applied', label: 'Grid Applied' },
  { key: 'permitting', label: 'Permitting' },
  { key: 'investment', label: 'Investment' },
  { key: 'active', label: 'Active' },
];

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'land', label: 'Land' },
  { key: 'grid', label: 'Grid' },
  { key: 'environment', label: 'Environment' },
  { key: 'planning', label: 'Planning' },
  { key: 'financials', label: 'Financials' },
];

// Per-tab automated data fields (shown below user forms)
const TAB_AUTO_FIELDS = {
  land: [
    ['zone_name', 'Zone type'], ['zone_code', 'Zone code'],
    ['total_area_ha', 'Area (ha)'], ['areal_ha', 'Parcel area (ha)'],
    ['constituent_count', 'Parcels in assemblage'], ['tvist', 'Dispute'],
    ['kommunenr', 'Municipality code'],
  ],
  grid: [
    ['nearest_grid_name', 'Substation'], ['nearest_grid_voltage_kv', 'Voltage (kV)'],
    ['nearest_grid_distance_km', 'Distance (km)'], ['dual_feed_available', 'Dual feed'],
    ['grid_redundancy', 'Redundancy'],
  ],
  environment: [
    ['flood_zone', 'Flood zone'], ['in_flood_extent', 'In flood extent'],
    ['nearest_protected_distance_km', 'Protected area dist (km)'],
    ['fi_cultural_heritage_count', 'Heritage sites'], ['red_listed_species_count', 'Red-listed species'],
    ['groundwater_class', 'Groundwater class'],
  ],
  planning: [
    ['zone_name', 'Current zoning'], ['zone_code', 'Zone code'],
    ['kommunenr', 'Municipality'],
  ],
  financials: [
    ['nearest_grid_distance_km', 'Grid distance (km)'],
    ['nearest_grid_voltage_kv', 'Voltage (kV)'],
  ],
};

// ── Key Facts (color-coded sidebar) ─────────────────────────────────
function KeyFacts({ enrichment }) {
  const e = enrichment || {};
  const facts = [
    { label: 'Grid', value: `${e.nearest_grid_voltage_kv || '—'} kV · ${e.nearest_grid_distance_km?.toFixed?.(1) || '—'} km`,
      color: (e.nearest_grid_distance_km || 99) <= 2 ? 'green' : (e.nearest_grid_distance_km || 99) <= 5 ? 'amber' : 'red' },
    { label: 'Dual feed', value: e.dual_feed_available === true ? 'Yes' : e.dual_feed_available === false ? 'No' : '—',
      color: e.dual_feed_available === true ? 'green' : e.dual_feed_available === false ? 'red' : 'gray' },
    { label: 'Area', value: (e.total_area_ha || e.areal_ha) ? `${Number(e.total_area_ha || e.areal_ha).toFixed(0)} ha` : '—',
      color: (e.total_area_ha || e.areal_ha || 0) >= 20 ? 'green' : 'amber' },
    { label: 'Flood', value: e.flood_zone === false ? 'Clear' : e.flood_zone === true ? 'In zone' : '—',
      color: e.flood_zone === false ? 'green' : e.flood_zone === true ? 'red' : 'gray' },
    { label: 'Slope', value: e.slope_pct != null ? `${Number(e.slope_pct).toFixed(0)}%` : '—',
      color: (e.slope_pct || 0) <= 5 ? 'green' : (e.slope_pct || 0) <= 15 ? 'amber' : 'red' },
    { label: 'Road', value: e.road_class_label || (e.nearest_road_distance_km != null ? `${e.nearest_road_distance_km.toFixed(1)} km` : '—'),
      color: e.road_class_label ? 'green' : 'gray' },
    { label: 'IXP', value: e.nearest_ixp_distance_km ? `${e.nearest_ixp_distance_km.toFixed(0)} km` : '—',
      color: (e.nearest_ixp_distance_km || 999) <= 50 ? 'green' : 'amber' },
    { label: 'Broadband', value: e.fi_broadband_1gbit ? '1 Gbit/s' : e.fi_broadband_100mbit ? '100 Mbit/s' : 'None',
      color: e.fi_broadband_1gbit ? 'green' : e.fi_broadband_100mbit ? 'amber' : 'red' },
    { label: 'Heritage', value: (e.fi_cultural_heritage_count ?? e.cultural_heritage_count) != null
        ? ((e.fi_cultural_heritage_count ?? e.cultural_heritage_count) === 0 ? 'None' : `${e.fi_cultural_heritage_count ?? e.cultural_heritage_count} found`) : '—',
      color: (e.fi_cultural_heritage_count ?? e.cultural_heritage_count ?? 0) === 0 ? 'green' : 'red' },
    { label: 'Species', value: e.red_listed_species_count != null ? (e.red_listed_species_count === 0 ? 'None' : `${e.red_listed_species_count} found`) : '—',
      color: (e.red_listed_species_count ?? 0) === 0 ? 'green' : 'red' },
    { label: 'Water', value: e.water_distance_km ? `${Number(e.water_distance_km).toFixed(1)} km` : '—',
      color: (e.water_distance_km || 99) <= 2 ? 'green' : (e.water_distance_km || 99) <= 5 ? 'amber' : 'red' },
    { label: 'Cooling', value: e.free_cooling_days_yr ? `${e.free_cooling_days_yr} d/yr` : '—',
      color: (e.free_cooling_days_yr || 0) >= 200 ? 'green' : 'amber' },
  ];

  return (
    <div className="key-facts">
      {facts.map(f => (
        <div key={f.label} className="key-fact-row">
          <span className="key-fact-label">{f.label}</span>
          <span className={`key-fact-value kf-${f.color}`}>{f.value}</span>
        </div>
      ))}
    </div>
  );
}

function formatAuto(val) {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'number') return Number.isInteger(val) ? String(val) : val.toFixed(2);
  return String(val);
}

// ═════════════════════════════════════════════════════════════════════
export default function ProjectDetail({ projectId }) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [enriching, setEnriching] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelText, setLabelText] = useState('');

  const { getProject, updateProject, reEnrich } = useProjects();

  const fetchProject = useCallback(async () => {
    const data = await getProject(projectId);
    if (data) {
      setProject(data);
      setLabelText(data.site_label);
    }
    setLoading(false);
  }, [projectId, getProject]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  const patchProject = async (updates) => {
    const updated = await updateProject(projectId, updates);
    if (updated) {
      setProject(updated);
      setLabelText(updated.site_label);
    }
  };

  const handleLabelSave = async () => {
    if (!labelText.trim() || labelText === project.site_label) {
      setEditingLabel(false);
      return;
    }
    await patchProject({ site_label: labelText.trim() });
    setEditingLabel(false);
  };

  const handleReEnrich = async () => {
    setEnriching(true);
    const updated = await reEnrich(projectId);
    if (updated) {
      setProject(updated);
      setLabelText(updated.site_label);
    }
    setEnriching(false);
  };

  if (loading) return <div className="pd-container"><div className="pd-loading">Loading project...</div></div>;
  if (!project) return (
    <div className="pd-container">
      <div className="pd-not-found">
        <p>Project not found</p>
        <a href="#/projects">Back to projects</a>
      </div>
    </div>
  );

  const enrichment = project.enrichment || {};
  const autoFields = TAB_AUTO_FIELDS[activeTab] || [];

  return (
    <div className="pd-container">
      {/* ── Context bar / breadcrumb ─────────────────────────── */}
      <div className="pd-breadcrumb">
        <a href="#/projects" className="pd-back">Projects</a>
        <span className="pd-sep">/</span>
        {editingLabel ? (
          <input className="pd-label-input" value={labelText}
                 onChange={e => setLabelText(e.target.value)}
                 onBlur={handleLabelSave}
                 onKeyDown={e => { if (e.key === 'Enter') handleLabelSave(); }}
                 autoFocus />
        ) : (
          <span className="pd-label" onClick={() => setEditingLabel(true)}>{project.site_label}</span>
        )}
        <select className="pd-stage" value={project.stage}
                onChange={e => patchProject({ stage: e.target.value })}>
          {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          <option value="dropped">Dropped</option>
        </select>
      </div>

      {/* ── Main layout ──────────────────────────────────────── */}
      <div className="pd-body">
        {/* Left sidebar */}
        <div className="pd-sidebar">
          {/* Satellite map */}
          <img src={mapUrl(project.lat, project.lon)} className="pd-map" alt="Site location"
               onError={e => { e.target.style.display = 'none'; }} />

          {/* Key Facts */}
          <div className="pd-section">
            <div className="pd-section-title">Key Facts</div>
            <KeyFacts enrichment={enrichment} />
          </div>

          {/* Comments */}
          <div className="pd-section">
            <CommentsPanel
              projectId={projectId}
              comments={project.comments}
              section={activeTab !== 'overview' ? activeTab : null}
              onAdd={fetchProject}
            />
          </div>
        </div>

        {/* Right content */}
        <div className="pd-content">
          {/* Tabs */}
          <div className="pd-tabs">
            {TABS.map(t => (
              <button key={t.key}
                      className={`pd-tab ${activeTab === t.key ? 'pd-tab-active' : ''}`}
                      onClick={() => setActiveTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="pd-tab-content">
            {activeTab === 'overview' ? (
              <ActivityTimeline activity={project.activity} />
            ) : (
              <>
                {/* User input section */}
                <div className="pd-user-section">
                  <div className="pd-section-label">Your data</div>
                  <WorkstreamForm
                    projectId={projectId}
                    workstream={activeTab}
                    data={project[activeTab]}
                    onSave={fetchProject}
                  />
                </div>

                {/* Sprint F · Feature 2 — per-tab document upload */}
                <DocumentSection
                  projectId={projectId}
                  tabKey={activeTab}
                  documents={project.documents}
                  onChange={fetchProject}
                />

                {/* Related automated data */}
                {autoFields.length > 0 && (
                  <div className="pd-auto-section">
                    <div className="pd-section-label">
                      Automated assessment
                      <button className="pd-refresh-btn" onClick={handleReEnrich} disabled={enriching}>
                        {enriching ? 'Refreshing...' : 'Refresh'}
                      </button>
                    </div>
                    <div className="pd-auto-grid">
                      {autoFields.map(([key, label]) => (
                        <div key={key} className="pd-auto-row">
                          <span className="pd-auto-label">{label}</span>
                          <span className="pd-auto-value">{formatAuto(enrichment[key])}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
